import { apiClient } from './client';
import type {
  CrmAccountOverview, CrmContactRole, CrmDeal, CrmTimelineEvent, CrmWorkActivity, CrmWorkCounts,
} from '../types';

// ─── Account 360 ────────────────────────────────────────────

export const crmAccountsApi = {
  /** Everything about one account in a single round trip. */
  overview: async (id: string): Promise<CrmAccountOverview> => {
    const { data } = await apiClient.get(`/crm/organizations/${id}/overview`);
    return data.data;
  },
};

// ─── Contact roles ──────────────────────────────────────────

export const crmContactRolesApi = {
  list: async (params: { organizationId?: string; dealId?: string; contactId?: string }): Promise<CrmContactRole[]> => {
    const { data } = await apiClient.get('/crm/contact-roles', { params });
    return data.data.roles;
  },
  create: async (body: {
    contactId: string; role: string; organizationId?: string; dealId?: string; isPrimary?: boolean; notes?: string;
  }): Promise<CrmContactRole> => {
    const { data } = await apiClient.post('/crm/contact-roles', body);
    return data.data.role;
  },
  update: async (id: string, body: { role?: string; isPrimary?: boolean; notes?: string }): Promise<CrmContactRole> => {
    const { data } = await apiClient.put(`/crm/contact-roles/${id}`, body);
    return data.data.role;
  },
  remove: async (id: string): Promise<void> => {
    await apiClient.delete(`/crm/contact-roles/${id}`);
  },
};

// ─── Work queues and the shared timeline ────────────────────

export type CrmWorkView =
  | 'today' | 'overdue' | 'upcoming' | 'unscheduled' | 'open'
  | 'deals-no-next-action' | 'deals-next-due' | 'deals-stale';

export const crmWorkApi = {
  list: async (view: CrmWorkView, mine: boolean): Promise<{ activities?: CrmWorkActivity[]; deals?: CrmDeal[] }> => {
    const { data } = await apiClient.get('/crm/work', { params: { view, ...(mine && { mine: '1' }) } });
    return data.data;
  },
  counts: async (mine: boolean): Promise<CrmWorkCounts> => {
    const { data } = await apiClient.get('/crm/work/counts', { params: mine ? { mine: '1' } : {} });
    return data.data.counts;
  },
  timeline: async (
    scope: { organizationId?: string; contactId?: string; dealId?: string; ticketId?: string },
    limit = 50,
  ): Promise<CrmTimelineEvent[]> => {
    const { data } = await apiClient.get('/crm/work/timeline', { params: { ...scope, limit } });
    return data.data.events;
  },
};
